import { Formik, FormikValues } from 'formik';
import React, { ChangeEvent, useContext, useEffect, useMemo, useState } from 'react';
import { ActivityIndicatorContext } from '../../../providers/ActivityIndicatorProvider/context';
import { Components } from './styled';
import ProjectTextField from '../../inputs/ProjectTextField';
import { ToastContext } from '../../../providers/ToastProvider/context';
import useModalState from '../../../hooks/useModalState';
import { isProjectError } from '../../../utils/errors';
import ProjectModal from '../../ProjectModal';
import ConfirmationModal from '../ConfirmationModal';
import { confirmationTexts } from '../ConfirmationModal/utils';
import { AppSettingsContext } from '../../../providers/AppSettingsProvider/context';
import useSearchDropdown, {
  getSearchDropdownItems,
  getSearchDropdownItemsFromTitleId,
  getTextFromSelectedItems,
  SearchDropdownItem,
} from '../../../hooks/useSearchDropdown';
import ProjectSearchDropdown from '../../ProjectSearchDropdown';
import { getDisplayNames } from '../../../api/utils';
import { Goal, GOAL_COLORS } from '../../../api/types/contentManagement';
import ProjectSelect from '../../inputs/ProjectSelect';
import { MinimalGoalSchema } from '../../../api/contentManagement/parser';
import { createGoal, updateGoal } from '../../../api/contentManagement';
import { uploadPublicFile } from '../../../utils/s3';
import { PUBLIC_BUCKET } from '../../../defines';
import { getFileExtension } from '../../../utils/s3/utils';
import { ReactComponent as AccessLinkIcon } from '../../../assets/icons/icon-grey-check-square-offset.svg';
import { getContentManagementUploadFileName } from '../../../api/contentManagement/utils';
import useCustomizedDropdown from '../../../hooks/useCustomizedDropdown';
import { getContentGroupsByIds } from '../../../api/contentGroup';
import SelectLesson from '../SelectLesson';
import { Lesson } from '../../../api/types/lesson';
const initialValues = {
  key: '',
  name: '',
  description: '',
  emoji: '',
  color: '',
  order: '' as string | number,
};

const ModalBody = ({
  modalState,
  onSave,
  onCancel,
}: {
  modalState?: Goal;
  onSave: (values: FormikValues) => Promise<void>;
  onCancel: () => void;
}) => {
  const { setIsLoading } = useContext(ActivityIndicatorContext);
  const { categories, pillarTypes, collectionContentGroups } = useContext(AppSettingsContext);

  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [favoritesLessons, setFavoritesLessons] = useState<Lesson[]>([]);
  const [imagePath, setImagePath] = useState<string | undefined>(modalState?.imagePath || undefined);
  const [imageUrl, setImageUrl] = useState<string | undefined>();
  const [imageUploadProgress, setImageUploadProgress] = useState<number | undefined>(undefined);

  const [linkedCategories, setLinkedCategories] = useState<string[]>([]);

  const categoryDropdownProps = useSearchDropdown({
    items: getSearchDropdownItems<string>(categories, (item) => item),
    dropdownType: 'basic',
  });

  const featuredCollectionDropdownProps = useSearchDropdown({
    items: getSearchDropdownItemsFromTitleId(collectionContentGroups),
    dropdownType: 'basic',
  });

  const pillarDropdownProps = useSearchDropdown({
    items: getSearchDropdownItems<string>(pillarTypes, (item) => getDisplayNames.pillarType(item) || ''),
    dropdownType: 'basic',
  });

  const { props: disabledCategoriesDropdownProps, setProps: setDisabledCategoriesDropdownProps } =
    useCustomizedDropdown({
      possibleValues: categories,
    });

  useEffect(() => {
    if (modalState?.id) {
      setIsEditing(true);
    }

    if (Array.isArray(modalState?.linkedPillars)) {
      pillarDropdownProps.setSelectedItems(() => {
        return modalState?.linkedPillars?.map((item) => ({
          value: item,
          label: getDisplayNames.pillarType(item),
          searchLabel: getDisplayNames.pillarType(item)?.toLowerCase(),
        })) as SearchDropdownItem[];
      });
    }
    if (Array.isArray(modalState?.linkedCategories)) {
      categoryDropdownProps.setSelectedItems(() => {
        return modalState?.linkedCategories?.map((item) => ({
          value: item,
          label: item,
          searchLabel: item.toLowerCase(),
        })) as SearchDropdownItem[];
      });
    }
    if (modalState?.disabledCategories && Array.isArray(modalState?.disabledCategories)) {
      setDisabledCategoriesDropdownProps(modalState?.disabledCategories);
    }
    if (modalState?.featuredCollection && Array.isArray(modalState?.featuredCollection)) {
      featuredCollectionDropdownProps.setSelectedItems(() => {
        return modalState?.featuredCollection?.map((item) => ({
          value: item.id,
          label: item.title,
          searchLabel: item.title?.toLowerCase(),
        })) as SearchDropdownItem[];
      });
    }
    if (modalState?.favoritesLessons && Array.isArray(modalState?.favoritesLessons)) {
      setFavoritesLessons(modalState?.favoritesLessons);
    }
  }, []);

  useEffect(() => {
    setLinkedCategories(categoryDropdownProps.selectedItems.map((item) => item.value));
  }, [categoryDropdownProps.selectedItems]);

  const goalInitialValues = {
    key: modalState?.key,
    name: modalState?.name,
    description: modalState?.description,
    emoji: modalState?.emoji ?? '',
    color: modalState?.color ?? '',
    order: modalState?.order ?? '',
  };

  const _onSubmit = async (values: FormikValues) => {
    await onSave?.({
      ...values,
      ...(pillarDropdownProps?.selectedItems?.length > 0 && {
        linkedPillars: pillarDropdownProps?.selectedItems?.map((item) => item.value),
      }),
      ...(categoryDropdownProps?.selectedItems?.length > 0 && {
        linkedCategories: categoryDropdownProps?.selectedItems?.map((item) => item.value),
      }),
      ...(featuredCollectionDropdownProps?.selectedItems?.length > 0 && {
        featuredCollection: await getContentGroupsByIds(
          featuredCollectionDropdownProps?.selectedItems?.map((item) => item.value),
        ),
      }),
      ...(favoritesLessons?.length > 0 && {
        favoritesLessons: favoritesLessons,
      }),
      imagePath: imagePath,
      ...(disabledCategoriesDropdownProps?.selectedItems?.length > 0 && {
        disabledCategories: disabledCategoriesDropdownProps?.selectedItems?.map((item) => item.value),
      }),
    });
  };

  const onUploadPublicFile = async (e: ChangeEvent<HTMLInputElement> | undefined, fileName: string, path: string) => {
    setIsLoading(true);
    const file = e?.target?.files?.[0];
    // @ts-ignore
    const newFile = new File([file], fileName, { type: file?.type });
    if (newFile) {
      if (e && e.target && e.target.value) {
        e.target.value = '';
      }

      await uploadPublicFile(newFile, path, (progress) => {
        setImageUploadProgress(progress);
        if (progress === 100) {
          setImagePath(path);
          const link = PUBLIC_BUCKET + path;
          setImageUrl(link);
          setImageUploadProgress(undefined);
        }
      });
    }
    setIsLoading(false);
  };

  const selectHandlerForImageFile = async (event: React.ChangeEvent<HTMLInputElement> | undefined) => {
    const file = event?.target?.files?.[0];

    if (file) {
      const extension = getFileExtension(file);
      const fileName = getContentManagementUploadFileName('goal');
      const path = 'images/'.concat(fileName).concat('.', extension || '');
      await onUploadPublicFile(event, fileName, path);
    }
  };

  const onAccessFileLink = () => {
    const _imageUrl = imageUrl || PUBLIC_BUCKET + imagePath;
    window.open(_imageUrl, '_blank');
  };

  const onClearImageLink = () => {
    setImagePath(undefined);
  };

  return (
    <Formik
      initialValues={modalState ? goalInitialValues : initialValues}
      validationSchema={MinimalGoalSchema}
      onSubmit={_onSubmit}>
      {({ values, handleChange, errors, touched, setFieldValue }) => (
        <Components.Form>
          <Components.ModalBodyContainer>
            <Components.FieldsWrapper>
              <Components.FieldStartAdornment variant={'minimal'} />
              <Components.FieldsContent>
                <Components.FieldWrapper>
                  <Components.FieldLabel>Key</Components.FieldLabel>
                  <ProjectTextField
                    value={values?.key}
                    onChangeText={handleChange}
                    name={'key'}
                    errorMessage={errors.key && touched.key ? errors?.key : undefined}
                    disabled={isEditing}
                    cursor={isEditing ? 'not-allowed' : undefined}
                    placeholder={'Enter goal key'}
                  />
                </Components.FieldWrapper>
                <Components.FieldWrapper>
                  <Components.FieldLabel>Value</Components.FieldLabel>
                  <ProjectTextField
                    value={values?.name}
                    onChangeText={handleChange}
                    name={'name'}
                    errorMessage={errors.name && touched.name ? errors?.name : undefined}
                    placeholder={'Enter goal name'}
                  />
                </Components.FieldWrapper>
              </Components.FieldsContent>
            </Components.FieldsWrapper>

            <Components.FieldsWrapper>
              <Components.FieldStartAdornment variant={'additional'} />
              <Components.FieldsContent>
                <Components.FieldWrapper>
                  <Components.FieldLabel>Description</Components.FieldLabel>
                  <ProjectTextField
                    value={values?.description}
                    onChangeText={handleChange}
                    name={'description'}
                    placeholder={'Enter description'}
                  />
                </Components.FieldWrapper>
                <Components.FieldWrapper>
                  <Components.FieldLabel>Emoji</Components.FieldLabel>
                  <ProjectTextField
                    value={values?.emoji ?? ''}
                    onChangeText={handleChange}
                    name={'emoji'}
                    placeholder={'e.g. 🪴'}
                  />
                </Components.FieldWrapper>
                <Components.FieldWrapper>
                  <Components.FieldLabel>Color</Components.FieldLabel>
                  <ProjectSelect
                    value={values?.color ?? ''}
                    onChangeSelectedItem={handleChange}
                    onClearValue={() => setFieldValue('color', '')}
                    name={'color'}
                    placeholder={'Select color'}>
                    {GOAL_COLORS.map((color) => (
                      <Components.MenuItem key={color} value={color}>
                        {color.charAt(0).toUpperCase() + color.slice(1)}
                      </Components.MenuItem>
                    ))}
                  </ProjectSelect>
                </Components.FieldWrapper>
                <Components.FieldWrapper>
                  <Components.FieldLabel>Order</Components.FieldLabel>
                  <ProjectTextField
                    value={values?.order === undefined || values?.order === null ? '' : String(values.order)}
                    onChangeText={handleChange}
                    name={'order'}
                    inputType={'number'}
                    placeholder={'e.g. 1'}
                  />
                  <Components.FieldBasic>
                    Lower numbers show first in &quot;Browse by outcome&quot;.
                  </Components.FieldBasic>
                </Components.FieldWrapper>
                <Components.FieldWrapper>
                  <Components.FieldLabel>Linked pillars</Components.FieldLabel>
                  <Components.Picker onClick={(e) => pillarDropdownProps.show(e)}>
                    {pillarDropdownProps?.selectedItems && pillarDropdownProps?.selectedItems?.length > 0 ? (
                      <Components.PickerLabel>
                        {getTextFromSelectedItems(pillarDropdownProps?.selectedItems)}
                      </Components.PickerLabel>
                    ) : (
                      <Components.NotSetLabel>None</Components.NotSetLabel>
                    )}
                    <Components.PickerIconWrapper>
                      <Components.PickerIcon />
                    </Components.PickerIconWrapper>
                  </Components.Picker>
                  <ProjectSearchDropdown {...pillarDropdownProps} />
                </Components.FieldWrapper>

                {/* Favorites lessons */}
                <Components.FieldWrapper>
                  <Components.FieldLabel>Favorites lessons</Components.FieldLabel>
                  <Components.UploadFileWrapper>
                    <Components.Picker onClick={() => setIsModalOpen(true)}>
                      {favoritesLessons && favoritesLessons?.length > 0 ? (
                        <Components.PickerLabel>
                          {favoritesLessons.map((lesson) => lesson.title).join(', ')}
                        </Components.PickerLabel>
                      ) : (
                        <Components.NotSetLabel>None</Components.NotSetLabel>
                      )}
                      <Components.PickerIconWrapper>
                        <Components.SelectIcon />
                      </Components.PickerIconWrapper>
                    </Components.Picker>
                    <SelectLesson
                      isModalOpen={isModalOpen}
                      onModalClose={() => setIsModalOpen(false)}
                      onSelectLesson={(data: Lesson[]) => {
                        setFavoritesLessons(data);
                      }}
                      initialSelectedLessons={favoritesLessons}
                    />
                    <Components.ClearButton onClick={() => setFavoritesLessons([])}>Clear</Components.ClearButton>
                  </Components.UploadFileWrapper>
                </Components.FieldWrapper>

                {/* Featured collection */}
                <Components.FieldWrapper>
                  <Components.FieldLabel>Featured collection</Components.FieldLabel>
                  <Components.Picker onClick={(e) => featuredCollectionDropdownProps.show(e)}>
                    {featuredCollectionDropdownProps?.selectedItems &&
                    featuredCollectionDropdownProps?.selectedItems?.length > 0 ? (
                      <Components.PickerLabel>
                        {getTextFromSelectedItems(featuredCollectionDropdownProps?.selectedItems)}
                      </Components.PickerLabel>
                    ) : (
                      <Components.NotSetLabel>None</Components.NotSetLabel>
                    )}
                    <Components.PickerIconWrapper>
                      <Components.PickerIcon />
                    </Components.PickerIconWrapper>
                  </Components.Picker>
                  <ProjectSearchDropdown {...featuredCollectionDropdownProps} />
                </Components.FieldWrapper>

                <Components.FieldWrapper>
                  <Components.FieldLabel>Linked categories</Components.FieldLabel>
                  <Components.Picker onClick={(e) => categoryDropdownProps.show(e)}>
                    {categoryDropdownProps?.selectedItems && categoryDropdownProps?.selectedItems?.length > 0 ? (
                      <Components.PickerLabel>
                        {getTextFromSelectedItems(categoryDropdownProps?.selectedItems)}
                      </Components.PickerLabel>
                    ) : (
                      <Components.NotSetLabel>None</Components.NotSetLabel>
                    )}
                    <Components.PickerIconWrapper>
                      <Components.PickerIcon />
                    </Components.PickerIconWrapper>
                  </Components.Picker>
                  <ProjectSearchDropdown {...categoryDropdownProps} />
                </Components.FieldWrapper>

                <Components.FieldWrapper>
                  <Components.FieldLabel>Image</Components.FieldLabel>
                  <Components.UploadFileWrapper>
                    <Components.InputMenu
                      type='file'
                      accept={'image/*'}
                      id='imageFile'
                      onChange={selectHandlerForImageFile}
                    />
                    <ProjectTextField value={imagePath || ''} disabled placeholder={'No image path'} />
                    {imagePath ? (
                      <>
                        <Components.IconWrapper onClick={() => onAccessFileLink()}>
                          <AccessLinkIcon />
                        </Components.IconWrapper>
                        <Components.ClearButton onClick={onClearImageLink}>Clear</Components.ClearButton>
                      </>
                    ) : (
                      <Components.UploadButton htmlFor={'imageFile'}>
                        {typeof imageUploadProgress === 'number' ? `${imageUploadProgress}%` : 'Upload Image'}
                      </Components.UploadButton>
                    )}
                  </Components.UploadFileWrapper>
                </Components.FieldWrapper>

                <Components.FieldWrapper>
                  <Components.FieldLabel>Disabled categories</Components.FieldLabel>
                  <Components.Picker onClick={(e) => disabledCategoriesDropdownProps.show(e)}>
                    {disabledCategoriesDropdownProps?.selectedItems &&
                    disabledCategoriesDropdownProps?.selectedItems?.length > 0 ? (
                      <Components.PickerLabel>
                        {getTextFromSelectedItems(disabledCategoriesDropdownProps?.selectedItems)}
                      </Components.PickerLabel>
                    ) : (
                      <Components.NotSetLabel>Not set</Components.NotSetLabel>
                    )}
                    <Components.PickerIconWrapper>
                      <Components.PickerIcon />
                    </Components.PickerIconWrapper>
                  </Components.Picker>
                  <ProjectSearchDropdown
                    {...disabledCategoriesDropdownProps}
                    facetFilter={(item) => linkedCategories.includes(item.value)}
                  />
                </Components.FieldWrapper>
              </Components.FieldsContent>
            </Components.FieldsWrapper>
          </Components.ModalBodyContainer>
          <Components.ModalButtonsContainer>
            <Components.ModalTertiaryButton onClick={() => onCancel?.()}>Cancel</Components.ModalTertiaryButton>
            <Components.ModalPrimaryButton type={'submit'}>Save</Components.ModalPrimaryButton>
          </Components.ModalButtonsContainer>
        </Components.Form>
      )}
    </Formik>
  );
};

const CreateOrEditGoalModal = ({
  isModalOpen,
  onModalClose,
  modalState,
  refreshCallback,
}: {
  isModalOpen: boolean;
  onModalClose: (refreshTrigger?: boolean) => void;
  modalState?: { goal?: Goal };
  refreshCallback?: () => Promise<void>;
}) => {
  const { setIsLoading } = useContext(ActivityIndicatorContext);
  const { showErrorToast, showSuccessToast } = useContext(ToastContext);

  const confirmationModal = useModalState<boolean>();

  const _onModalClose = (refreshTrigger?: boolean) => {
    confirmationModal.open(refreshTrigger || false);
  };

  const _onSave = async (values: FormikValues) => {
    const data = {
      name: values?.name,
      description: values?.description,
      emoji: (values?.emoji || '').trim() || null,
      color: values?.color || null,
      order:
        values?.order === '' || values?.order === undefined || values?.order === null ? null : Number(values.order),
      linkedPillars: values?.linkedPillars && Array.isArray(values?.linkedPillars) ? values?.linkedPillars : [],
      linkedCategories:
        values?.linkedCategories && Array.isArray(values?.linkedCategories) ? values?.linkedCategories : [],
      imagePath: values?.imagePath || null,
      disabledCategories:
        values?.disabledCategories && Array.isArray(values?.disabledCategories) ? values?.disabledCategories : [],
      featuredCollection:
        values?.featuredCollection && Array.isArray(values?.featuredCollection) ? values?.featuredCollection : [],
      favoritesLessons:
        values?.favoritesLessons && Array.isArray(values?.favoritesLessons) ? values?.favoritesLessons : [],
    };

    setIsLoading(true);
    const result =
      modalState && modalState?.goal?.id
        ? await updateGoal({ ...data, id: modalState?.goal?.id })
        : await createGoal({ ...data, key: values?.key });
    setIsLoading(false);

    const resultError = isProjectError(result);
    if (resultError) {
      showErrorToast(resultError);
    } else {
      modalState && modalState?.goal?.id
        ? showSuccessToast('The changes were saved successfully')
        : showSuccessToast('You successfully added a goal');
      onModalClose?.(true);
      refreshCallback?.().then();
    }
  };

  const onConfirmCancel = (value: boolean) => {
    onModalClose?.(value);
  };

  return (
    <>
      <ProjectModal
        bodyChildren={<ModalBody modalState={modalState?.goal} onSave={_onSave} onCancel={_onModalClose} />}
        isModalOpen={isModalOpen}
        onModalClose={_onModalClose}
        modalTitle={modalState?.goal ? `Edit goal` : 'Create new goal'}
        titleSuffix={modalState?.goal ? `(ID: ${modalState?.goal?.id})` : undefined}
        disableCloseOnClickAway
      />
      <ConfirmationModal
        {...confirmationModal}
        confirmationText={confirmationTexts.lessons.cancelEditing}
        onConfirm={onConfirmCancel}
      />
    </>
  );
};

export default CreateOrEditGoalModal;
